import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { apiClient, Project } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { useDebounce } from "@/hooks/use-debounce";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArtieScoreData } from "@/types/artie-score";
import { logger } from "@/lib/logger";
import {
  PieChart,
  Plus,
  Search,
  List,
  Grid,
  FileText,
  Clock,
  MoreVertical,
  Film,
  File,
  Presentation,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import logo from '../assets/images/logo-artie.png';
import { Sidebar } from "@/components/Sidebar";
import { DashboardProjectCard } from "@/components/DashboardProjectCard";
import { DashboardProjectItem } from "@/components/DashboardProjectItem";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const Dashboard = () => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOption, setSortOption] = useState("updated");

  const [page, setPage] = useState(1);
  const limit = 20;

  // Log dashboard access
  useEffect(() => {
    logger.info("Dashboard page accessed", {
      userId: user?._id,
      userEmail: user?.email,
      timestamp: new Date().toISOString(),
      page: "dashboard"
    });
  }, [user]);

  // Log search and filter usage
  useEffect(() => {
    if (debouncedSearch) {
      logger.debug("Dashboard search performed", {
        searchQuery: debouncedSearch,
        userId: user?._id,
        timestamp: new Date().toISOString()
      });
    }
  }, [debouncedSearch, user]);

  useEffect(() => {
    if (statusFilter !== "all") {
      logger.debug("Dashboard filter applied", {
        filter: statusFilter,
        userId: user?._id,
        timestamp: new Date().toISOString()
      });
    }
  }, [statusFilter, user]);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, sortOption]);

  // Fetch projects
  const { data: projectData, isLoading: projectsLoading } = useQuery({
    queryKey: ["projects", user?._id, page, debouncedSearch, statusFilter, sortOption],
    queryFn: () => apiClient.getProjects(page, limit, debouncedSearch, statusFilter, sortOption),
    enabled: !!user?._id,
    staleTime: 0, // Always refetch in background to ensure latest script status
  });

  const projects = projectData?.projects || [];
  const totalProjects = projectData?.total || 0;
  const totalPages = Math.ceil(totalProjects / limit);

  // Fetch scores for all projects
  const projectIds = projects?.map(p => p._id).join(',') || '';
  const { data: projectScores } = useQuery({
    queryKey: ["projectScores", user?._id, projectIds],
    queryFn: async ({ signal }) => {
      if (!projects || projects.length === 0) return {};

      const scores: Record<string, number> = {};
      await Promise.all(
        projects.map(async (project) => {
          if (project.status === 'analysis_complete' || project.status === 'pending') {
            try {
              const score = await apiClient.getArtieScoreByProject(project._id) as ArtieScoreData;
              if (score && score.artie_score) {
                // Save to individual query cache for instant loading when clicked
                queryClient.setQueryData(["artieScore", project._id], score);
                scores[project._id] = score.artie_score;
              }
            } catch (error) {
              // Score not found, skip
            }
          }
        })
      );
      return scores;
    },
    enabled: !!user?._id && !!projects && projects.length > 0,
    staleTime: 300000, // 5 minutes
  });



  return (
    <div className="min-h-screen overflow-hidden selection:bg-primary selection:text-primary-foreground relative bg-background text-foreground flex font-geist">

      {/* Zone 1: Left Sidebar (Navigation) */}
      <Sidebar />

      {/* Content Wrapper (Main) */}
      <div
        id="main-wrapper"
        className="flex flex-1 pl-0 pt-16 md:pt-0 transition-all duration-300 h-screen w-full"
      >
        {/* Zone 2: Main Content Area */}
        <main className="flex-1 min-w-0 flex flex-col z-10 overflow-y-auto no-scrollbar bg-transparent h-full py-6 px-4 md:pt-8 md:pr-10 md:pb-8 md:pl-10 relative">
          {/* Header Section */}
          <header className="flex flex-col md:flex-row mb-6 md:mb-10 items-center justify-between gap-4 md:gap-6">
            {/* Left Side: Fun Profile & Context */}
            <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
              <div>
                <h1 className="text-3xl font-semibold text-foreground tracking-tight font-cabinet">
                  My Projects
                </h1>
                <p className="text-muted-foreground font-geist text-sm mt-1 max-w-sm">
                  Manage and analyze your active scripts in one place.
                </p>
              </div>
            </div>

            {/* Right Side: Actions */}
            <div className="flex items-center gap-3 shrink-0">
              {/* Analytics Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    disabled
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground bg-card/60 backdrop-blur-sm border border-border shadow-sm font-geist opacity-50 cursor-not-allowed"
                  >
                    <PieChart className="w-4 h-4" />
                    <span>Analytics</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Coming Soon</p>
                </TooltipContent>
              </Tooltip>

              {/* New Project Action */}
              <Link to="/projects/create/step-2" state={{ isNewProject: true }}>
                <button
                  className="group flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm font-geist"
                >
                  <Plus className="w-4 h-4 stroke-[2]" />
                  <span>New Project</span>
                </button>
              </Link>
            </div>
          </header>

          {/* Filter & Search Bar */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            {/* Search */}
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                <Search className="w-4 h-4" />
              </div>
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 bg-card/60 backdrop-blur-sm border border-border rounded-lg text-sm placeholder:text-muted-foreground text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-sm font-geist h-[38px]"
              />
            </div>

            {/* Filters */}
            <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar items-center">
              {/* View Toggle (Visual Only for now) */}
              <div className="flex items-center p-1 bg-secondary rounded-lg border border-border mr-2 h-[38px]">
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-1.5 rounded transition-all ${viewMode === 'list' ? 'bg-card text-primary shadow-sm ring-1 ring-black/5' : 'text-muted-foreground hover:text-foreground hover:bg-card'}`}
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-1.5 rounded transition-all ${viewMode === 'grid' ? 'bg-card text-primary shadow-sm ring-1 ring-black/5' : 'text-muted-foreground hover:text-foreground hover:bg-card'}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="custom-select bg-card/60 backdrop-blur-sm border border-border text-foreground text-xs font-medium rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 hover:bg-card cursor-pointer min-w-[100px] shadow-sm font-geist h-[38px]"
              >
                <option value="all">Status: All</option>
                <option value="completed">Score Ready</option>
                <option value="analyzing">Analyzing</option>
                <option value="draft">Draft</option>
              </select>
              <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="custom-select bg-card/60 backdrop-blur-sm border border-border text-foreground text-xs font-medium rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 hover:bg-card cursor-pointer min-w-[140px] shadow-sm font-geist h-[38px]"
              >
                <option value="updated">Sort: Updated</option>
                <option value="score">Sort: Score</option>
                <option value="name">Sort: Name</option>
              </select>
            </div>
          </div>

          {/* Projects Grid (Folder Design) */}
          <div className={
            viewMode === 'grid'
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-6 mb-8 pt-4"
              : "flex flex-col gap-4 mb-8 pt-4"
          }>

            {projectsLoading ? (
              // Loading Skeletons
              [1, 2, 3].map((i) => (
                viewMode === 'grid' ? (
                  <div key={i} className="folder-wrapper group relative flex flex-col mt-3">
                    <div className="folder-shadow relative flex flex-col w-full">
                      <div className="relative z-20 h-[22px] w-[35%] bg-card border-t border-l border-r border-border rounded-t-lg -mb-[1px] ml-[1px]"></div>
                      <div className="relative z-10 bg-card border border-border rounded-b-xl rounded-tr-xl p-5 h-full min-h-[180px] animate-pulse">
                        <div className="h-6 w-3/4 bg-muted rounded mb-4"></div>
                        <div className="h-4 w-full bg-muted rounded mb-2"></div>
                        <div className="h-4 w-1/2 bg-muted rounded"></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div key={i} className="w-full h-24 bg-card border border-border rounded-xl animate-pulse"></div>
                )
              ))
            ) : projects.length === 0 ? (
              <div className="col-span-full flex flex-col items-center justify-center p-10 text-center">
                <p className="text-muted-foreground font-geist">No projects found. Create your first project to get started!</p>
              </div>
            ) : (
              projects.map((project) => (
                viewMode === 'grid' ? (
                  <DashboardProjectCard
                    key={project._id}
                    project={project}
                    score={projectScores?.[project._id]}
                  />
                ) : (
                  <DashboardProjectItem
                    key={project._id}
                    project={project}
                    score={projectScores?.[project._id]}
                  />
                )
              ))
            )}
          </div>

          {/* Pagination */}
          {totalProjects > 0 && (
            <div className="flex items-center justify-between px-2 mb-2 mt-auto">
              <span className="text-xs text-muted-foreground font-geist">
                Showing <span className="font-medium text-foreground">{(page - 1) * limit + 1}-{Math.min(page * limit, totalProjects)}</span> of{" "}
                <span className="font-medium text-foreground">{totalProjects}</span> projects
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 border border-border bg-card/60 rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <div className="flex gap-1 font-geist">
                  <button className="px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-medium">
                    {page}
                  </button>
                  <span className="text-xs flex items-center text-muted-foreground">of {totalPages}</span>
                </div>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 border border-border bg-card/60 rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};